import {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';

import { PRODUCT_EXPIRY_DATE_POLICY } from '../../../common/constants';
import {
	DISCARD_PENDING_STOCK,
	MOVE_PENDING_STOCK_TO_WAREHOUSE,
} from '../../../common/graphql/pendingStock';
import { useUserContext } from '../../../common/utils/context';
import {
	isProductExpiryDateRecommended,
	isProductExpiryDateRequired,
} from '../../../common/utils/warehousing/productExpiryPolicy';
import { serializeDateKeyForTimezone } from '../../../common/utils/warehousing/formatExpiryBatchSummary';

import {
	buildDefaultBatchRows,
	buildInitialBatchRows,
	getBatchRowsQuantity,
	getFirstBatchExpiryDate,
	hasExpiredBatchExpiryDate,
	hasShortDatedBatchExpiryDate,
	hasUnknownBatchExpiryDate,
	normalizeBatchExpiryDatesForSave,
} from './PendingStockHelpers';

// Batch rows, expiry checks and the move/discard mutations behind the resolve dialog.
const usePendingStockResolve = ({
	item,
	quantityInUnits,
	timezone,
	refetchList,
}) => {
	const { tenantSettings } = useUserContext();

	const [movePendingStockToWarehouse, { loading: moveLoading }] = useMutation(MOVE_PENDING_STOCK_TO_WAREHOUSE);
	const [discardPendingStock, { loading: discardLoading }] = useMutation(DISCARD_PENDING_STOCK);

	const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
	const [resolveBatchRows, setResolveBatchRows] = useState(() => buildInitialBatchRows(item, quantityInUnits, timezone));

	const normalizedResolveQuantity = useMemo(() => getBatchRowsQuantity(resolveBatchRows), [resolveBatchRows]);
	const isResolveShortDated = useMemo(() => (
		hasShortDatedBatchExpiryDate(resolveBatchRows, timezone)
	), [resolveBatchRows, timezone]);
	const hasExpiredResolveStock = useMemo(() => (
		hasExpiredBatchExpiryDate(resolveBatchRows, timezone)
	), [resolveBatchRows, timezone]);
	const hasUnknownResolveExpiryDate = useMemo(() => (
		hasUnknownBatchExpiryDate(resolveBatchRows)
	), [resolveBatchRows]);
	const expiryPolicyProduct = {
		...item?.product,
		...item?.warehouseProduct?.product,
	};
	const resolvedOwnerPolicy = item?.expiryPolicyContext?.policy;
	const resolveExpiryDateRequired = resolvedOwnerPolicy
		? resolvedOwnerPolicy === PRODUCT_EXPIRY_DATE_POLICY.REQUIRED
		: isProductExpiryDateRequired({
			product: expiryPolicyProduct,
			tenantSettings,
		});
	const resolveExpiryDateRecommended = resolvedOwnerPolicy
		? resolvedOwnerPolicy === PRODUCT_EXPIRY_DATE_POLICY.RECOMMENDED
		: isProductExpiryDateRecommended({
			product: expiryPolicyProduct,
			tenantSettings,
		});
	const isRequiredResolveExpiryDateMissing = resolveExpiryDateRequired && hasUnknownResolveExpiryDate;
	const canSubmitResolve = Boolean(item?.id) && normalizedResolveQuantity > 0 && normalizedResolveQuantity <= quantityInUnits;

	useEffect(() => {
		setResolveBatchRows(buildInitialBatchRows(item, quantityInUnits, timezone));
	}, [item, quantityInUnits, timezone]);

	const updateResolveBatchRow = useCallback((index, field, value) => {
		setResolveBatchRows(previous => previous.map((row, rowIndex) => (
			rowIndex === index ? { ...row, [field]: value } : row
		)));
	}, []);

	const addResolveBatchRow = useCallback(() => {
		setResolveBatchRows(previous => [...previous, ...buildDefaultBatchRows(null)]);
	}, []);

	const removeResolveBatchRow = useCallback((index) => {
		setResolveBatchRows(previous => (
			previous.length > 1 ? previous.filter((_, rowIndex) => rowIndex !== index) : previous
		));
	}, []);

	const moveToWarehouse = useCallback(async () => {
		if (!canSubmitResolve || hasExpiredResolveStock || isRequiredResolveExpiryDateMissing) {
			return;
		}

		const batchExpiryDates = normalizeBatchExpiryDatesForSave(resolveBatchRows, timezone);
		const firstExpiryDate = getFirstBatchExpiryDate(resolveBatchRows);

		try {
			await movePendingStockToWarehouse({
				variables: {
					id: item.id,
					quantity: normalizedResolveQuantity,
					expiryDate: serializeDateKeyForTimezone(firstExpiryDate, timezone),
					batchExpiryDates,
				},
			});
			await refetchList?.();
			setIsResolveDialogOpen(false);
			Toast.show('Pending stock moved to warehouse');
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to move pending stock');
		}
	}, [
		canSubmitResolve,
		hasExpiredResolveStock,
		isRequiredResolveExpiryDateMissing,
		item?.id,
		movePendingStockToWarehouse,
		normalizedResolveQuantity,
		refetchList,
		resolveBatchRows,
		timezone,
	]);

	const discardFromPendingStock = useCallback(async () => {
		if (!canSubmitResolve) {
			return;
		}

		const batchExpiryDates = normalizeBatchExpiryDatesForSave(resolveBatchRows, timezone);

		try {
			await discardPendingStock({
				variables: {
					id: item.id,
					quantity: normalizedResolveQuantity,
					batchExpiryDates,
				},
			});
			await refetchList?.();
			setIsResolveDialogOpen(false);
			Toast.show('Pending stock discarded');
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to discard pending stock');
		}
	}, [
		canSubmitResolve,
		discardPendingStock,
		item?.id,
		normalizedResolveQuantity,
		refetchList,
		resolveBatchRows,
		timezone,
	]);

	return {
		moveLoading,
		discardLoading,
		isResolveDialogOpen,
		setIsResolveDialogOpen,
		resolveBatchRows,
		updateResolveBatchRow,
		addResolveBatchRow,
		removeResolveBatchRow,
		normalizedResolveQuantity,
		isResolveShortDated,
		hasExpiredResolveStock,
		hasUnknownResolveExpiryDate,
		resolveExpiryDateRecommended,
		isRequiredResolveExpiryDateMissing,
		canSubmitResolve,
		moveToWarehouse,
		discardFromPendingStock,
	};
};

export default usePendingStockResolve;
